/**
 * Grade do ano — as mesmas colunas da aba "PO aa" da planilha, na mesma ordem,
 * com a cor da linha pelo status (D1) e as faixas de fechamento no lugar original.
 * Duplo clique edita a célula (Enter grava, Esc desiste). A cor troca no ponto colorido.
 * Colunas com divisões e largura ajustável (arrastar a divisão do cabeçalho; duplo
 * clique ajusta ao conteúdo). "⋯" no fim da linha (ou a tecla Delete) exclui.
 */
import { createEffect, createMemo, createSignal, For, Match, onCleanup, Show, Switch } from 'solid-js'
import { Dropdown } from '@ui/components/Dropdown'
import type { FechamentoGrade, PedidoLinha, StatusPedido } from '@shared/types'
import type { PedidoInput } from '@shared/validation'
import { lerItens } from '../../../../../packages/core/services/pedidos/textos'
import { api } from '../../nucleo/cliente'
import { mensagemErro } from '../../lib/erro'
import { corDoStatus, lerNumeroBR, numero, paraEntrada, corExibicao } from './formato'

export type Campo =
  | 'stat' | 'cliente' | 'pedido' | 'orderWerc' | 'proposta' | 'dataEntrega' | 'entregou'
  | 'transportadora' | 'valor' | 'aReceber' | 'nf' | 'prevPgmto' | 'ok' | 'itens'

/** Cabeçalho igual ao da planilha (linha 3 das abas PO). `px` = largura padrão. */
export const COLUNAS: Array<{ campo: Campo; titulo: string; px: number; numero?: boolean }> = [
  { campo: 'stat', titulo: 'STAT.', px: 96 },
  { campo: 'cliente', titulo: 'Cliente', px: 180 },
  { campo: 'pedido', titulo: 'Pedido - dia data', px: 200 },
  { campo: 'orderWerc', titulo: 'Order Werc', px: 112 },
  { campo: 'proposta', titulo: 'Proposta', px: 120 },
  { campo: 'dataEntrega', titulo: 'Data entrega', px: 110 },
  { campo: 'entregou', titulo: 'Entregou', px: 140 },
  { campo: 'transportadora', titulo: 'Transportadora', px: 140 },
  { campo: 'valor', titulo: 'Valor', px: 120, numero: true },
  { campo: 'aReceber', titulo: 'A receber', px: 120, numero: true },
  { campo: 'nf', titulo: 'NF', px: 72 },
  { campo: 'prevPgmto', titulo: 'Prev. pgmto.', px: 120 },
  { campo: 'ok', titulo: 'OK', px: 88 },
  { campo: 'itens', titulo: 'Itens', px: 380 },
]
const L_STATUS = 34
const L_ACOES = 40
const MIN_COL = 48

// Larguras ajustadas ficam NESTE computador (conveniência; sem armazenamento, volta ao padrão).
const CHAVE_LARGURAS = 'werc.pedidos.larguras'
type Larguras = Record<Campo, number>
const padrao = (): Larguras => Object.fromEntries(COLUNAS.map((c) => [c.campo, c.px])) as Larguras
function lerLarguras(): Larguras {
  const l = padrao()
  try {
    const salvo = JSON.parse(localStorage.getItem(CHAVE_LARGURAS) ?? '{}') as Partial<Larguras>
    for (const c of COLUNAS) {
      const v = salvo[c.campo]
      if (typeof v === 'number' && Number.isFinite(v)) l[c.campo] = Math.max(MIN_COL, v)
    }
  } catch {
    /* sem armazenamento: padrão */
  }
  return l
}
function guardarLarguras(l: Larguras) {
  try {
    localStorage.setItem(CHAVE_LARGURAS, JSON.stringify(l))
  } catch {
    /* sem armazenamento: vale só nesta sessão */
  }
}

type ItemGrade = { tipo: 'pedido'; p: PedidoLinha } | { tipo: 'faixa'; f: FechamentoGrade }

function textoCelula(p: PedidoLinha, c: Campo): string {
  if (c === 'valor') return p.valor !== null ? numero(p.valor) : (p.valorTexto ?? '')
  if (c === 'aReceber') return p.aReceber !== null ? numero(p.aReceber) : (p.aReceberTexto ?? '')
  return (p[c] as string | null) ?? ''
}

/** Aplica a edição de UMA célula sobre a entrada completa do pedido. */
function aplicar(e: PedidoInput, c: Campo, texto: string): PedidoInput {
  if (c === 'valor' || c === 'aReceber') {
    const n = lerNumeroBR(texto)
    if (Number.isNaN(n)) throw new Error('Valor inválido. Use números, como 24.000,50.')
    return { ...e, [c]: n }
  }
  if (c === 'itens') return { ...e, itens: lerItens(texto).map((i) => ({ quantidade: i.quantidade, produto: i.produto, complemento: i.complemento })) }
  return { ...e, [c]: texto }
}

const emCampoDeTexto = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))

export function Grade(props: {
  pedidos: PedidoLinha[]
  fechamentos: FechamentoGrade[]
  status: StatusPedido[] | undefined
  mostrarFaixas: boolean
  podeEditar: boolean
  selecionado: number | null
  aoSelecionar: (id: number) => void
  aoEditar: (id: number) => void
  aoExcluir: (p: PedidoLinha) => void
  aoMudar: () => void
  aoErro: (msg: string) => void
  rolarParaFim: number
}) {
  let caixa: HTMLDivElement | undefined
  const [editando, setEditando] = createSignal<{ id: number; campo: Campo } | null>(null)
  const [gravando, setGravando] = createSignal<number | null>(null)
  const [larguras, setLarguras] = createSignal<Larguras>(lerLarguras())
  const larguraTotal = () => L_STATUS + L_ACOES + COLUNAS.reduce((s, c) => s + larguras()[c.campo], 0)

  // Mantém a MESMA linha (objeto) quando o pedido não mudou: ao recarregar depois de
  // uma gravação, o <For> só refaz as linhas alteradas e a rolagem não pula.
  let cache = new Map<string, { chave: string; item: ItemGrade }>()
  const reusar = (id: string, valor: PedidoLinha | FechamentoGrade, novo: () => ItemGrade, prox: typeof cache) => {
    const chave = JSON.stringify(valor)
    const antes = cache.get(id)
    const item = antes && antes.chave === chave ? antes.item : novo()
    prox.set(id, { chave, item })
    return item
  }
  const itens = createMemo<ItemGrade[]>(() => {
    const prox: typeof cache = new Map()
    const lista: ItemGrade[] = props.pedidos.map((p) => reusar(`p${p.id}`, p, () => ({ tipo: 'pedido', p }), prox))
    if (props.mostrarFaixas)
      for (const f of props.fechamentos) lista.push(reusar(`f${f.id}`, f, () => ({ tipo: 'faixa', f }), prox))
    cache = prox
    const pos = (i: ItemGrade) => (i.tipo === 'pedido' ? i.p.posicao : i.f.posicao)
    return lista.sort((a, b) => pos(a) - pos(b))
  })

  // Como na planilha: os pedidos mais novos ficam embaixo. Abre já no fim.
  let ultimoPedido = -1
  const rolar = () => {
    if (caixa && props.pedidos.length > 0 && props.rolarParaFim !== ultimoPedido) {
      ultimoPedido = props.rolarParaFim
      requestAnimationFrame(() => caixa && (caixa.scrollTop = caixa.scrollHeight))
    }
  }
  createEffect(() => {
    void props.pedidos.length
    rolar()
  })

  // pedido selecionado (ex.: acabou de ser criado) sempre visível
  createEffect(() => {
    const id = props.selecionado
    void itens()
    if (id !== null) requestAnimationFrame(() => caixa?.querySelector(`[data-id="${id}"]`)?.scrollIntoView({ block: 'nearest' }))
  })

  // tecla Delete exclui o pedido selecionado (sempre com confirmação)
  const teclas = (e: KeyboardEvent) => {
    if (e.key !== 'Delete' || !props.podeEditar || props.selecionado === null || editando()) return
    if (emCampoDeTexto(e.target) || document.querySelector('[role="dialog"]')) return
    const p = props.pedidos.find((x) => x.id === props.selecionado)
    if (p) {
      e.preventDefault()
      props.aoExcluir(p)
    }
  }
  window.addEventListener('keydown', teclas)
  onCleanup(() => window.removeEventListener('keydown', teclas))

  // ---------------------------------------------- largura das colunas (arrastar)
  const arrastar = (campo: Campo, e: PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const alca = e.currentTarget as HTMLElement
    const x0 = e.clientX
    const w0 = larguras()[campo]
    alca.setPointerCapture(e.pointerId)
    const mover = (ev: PointerEvent) =>
      setLarguras({ ...larguras(), [campo]: Math.min(1200, Math.max(MIN_COL, Math.round(w0 + ev.clientX - x0))) })
    const soltar = () => {
      alca.removeEventListener('pointermove', mover)
      alca.removeEventListener('pointerup', soltar)
      alca.removeEventListener('pointercancel', soltar)
      guardarLarguras(larguras())
    }
    alca.addEventListener('pointermove', mover)
    alca.addEventListener('pointerup', soltar)
    alca.addEventListener('pointercancel', soltar)
  }
  /** Duplo clique na divisão: a coluna fica do tamanho do maior texto. */
  const ajustarAoConteudo = (campo: Campo) => {
    const ctx = document.createElement('canvas').getContext('2d')
    if (!ctx || !caixa) return
    ctx.font = getComputedStyle(caixa).font
    const col = COLUNAS.find((c) => c.campo === campo)!
    let w = ctx.measureText(col.titulo).width + 28
    for (const p of props.pedidos) w = Math.max(w, ctx.measureText(textoCelula(p, campo)).width + 22)
    const l = { ...larguras(), [campo]: Math.round(Math.min(campo === 'itens' ? 900 : 480, Math.max(MIN_COL, w))) }
    setLarguras(l)
    guardarLarguras(l)
  }
  const larguraPadrao = () => {
    setLarguras(padrao())
    guardarLarguras(padrao())
  }

  const gravar = async (id: number, campo: Campo, texto: string) => {
    setEditando(null)
    setGravando(id)
    try {
      const d = await api.pedidos.get(id)
      if (!d) throw new Error('Pedido não encontrado.')
      if (texto === textoCelula(d, campo)) return
      await api.pedidos.update(id, aplicar(paraEntrada(d), campo, texto))
      props.aoMudar()
    } catch (e) {
      props.aoErro(mensagemErro(e))
    } finally {
      setGravando(null)
    }
  }

  const trocarStatus = async (id: number, statusId: number) => {
    try {
      await api.pedidos.definirStatus(id, statusId)
      props.aoMudar()
    } catch (e) {
      props.aoErro(mensagemErro(e))
    }
  }

  const divisao = 'border-r border-edge'
  return (
    <div ref={caixa} class="ped-rolagem h-full overflow-auto">
      <table class="text-[13px] border-separate border-spacing-0" style={{ 'table-layout': 'fixed', width: `${larguraTotal()}px` }}>
        <colgroup>
          <col style={{ width: `${L_STATUS}px` }} />
          <For each={COLUNAS}>{(c) => <col style={{ width: `${larguras()[c.campo]}px` }} />}</For>
          <col style={{ width: `${L_ACOES}px` }} />
        </colgroup>
        <thead class="sticky top-0 z-20">
          <tr class="text-left text-ink-subtle">
            <th class={`sticky left-0 z-30 bg-panel border-b ${divisao} p-0`}>
              <button
                type="button"
                class="w-full py-2 text-ink-placeholder hover:text-ink"
                title="Voltar as colunas para a largura padrão"
                aria-label="Voltar as colunas para a largura padrão"
                onClick={larguraPadrao}
              >
                ↺
              </button>
            </th>
            <For each={COLUNAS}>
              {(c) => (
                <th
                  class={`relative bg-panel border-b ${divisao} font-medium px-2 py-2 whitespace-nowrap overflow-hidden text-ellipsis select-none`}
                  classList={{ 'text-right': c.numero, 'sticky z-30': c.campo === 'cliente' }}
                  style={c.campo === 'cliente' ? { left: `${L_STATUS}px` } : undefined}
                >
                  {c.titulo}
                  <span
                    class="absolute top-0 right-0 h-full w-2 cursor-col-resize hover:bg-accent/40 active:bg-accent/60"
                    title="Arraste para mudar a largura · duplo clique ajusta ao conteúdo"
                    onPointerDown={(e) => arrastar(c.campo, e)}
                    onDblClick={(e) => (e.stopPropagation(), ajustarAoConteudo(c.campo))}
                  />
                </th>
              )}
            </For>
            <th class="sticky right-0 z-30 bg-panel border-b border-l border-edge" aria-label="Ações" />
          </tr>
        </thead>
        <tbody>
          <For each={itens()}>
            {(it) => (
              <Switch>
                <Match when={it.tipo === 'faixa' && (it as { f: FechamentoGrade }).f}>
                  {(f) => (
                    <tr class="ped-faixa">
                      <td class={`sticky left-0 z-10 border-b ${divisao}`} />
                      <td colSpan={COLUNAS.length} class="border-b border-edge px-2 py-1.5 text-xs font-medium text-ink-muted">
                        <span class="sticky" style={{ left: `${L_STATUS + 8}px` }}>
                          {f().rotulo}
                        </span>
                      </td>
                      <td class="sticky right-0 z-10 border-b border-l border-edge" />
                    </tr>
                  )}
                </Match>
                <Match when={it.tipo === 'pedido' && (it as { p: PedidoLinha }).p}>
                  {(p) => (
                    <tr
                      class="ped-linha text-ink cursor-default"
                      style={{ '--cor': corDoStatus(props.status, p().statusId) ?? 'transparent' }}
                      data-selecionada={props.selecionado === p().id}
                      data-id={p().id}
                      onClick={() => props.aoSelecionar(p().id)}
                    >
                      <td class={`sticky left-0 z-10 border-b ${divisao} px-1`}>
                        <PontoStatus
                          status={props.status}
                          atual={p().statusId}
                          podeEditar={props.podeEditar}
                          aoTrocar={(s) => void trocarStatus(p().id, s)}
                        />
                      </td>
                      <For each={COLUNAS}>
                        {(c) => (
                          <td
                            class={`border-b ${divisao} px-2 py-1.5 align-top overflow-hidden`}
                            classList={{
                              'text-right tabular-nums': c.numero,
                              'sticky z-10 font-medium': c.campo === 'cliente',
                              'whitespace-nowrap text-ellipsis': c.campo !== 'itens',
                              'break-words': c.campo === 'itens',
                              'opacity-50': gravando() === p().id,
                              'italic text-ink-muted': c.numero && p()[c.campo] === null,
                            }}
                            style={c.campo === 'cliente' ? { left: `${L_STATUS}px` } : undefined}
                            title={textoCelula(p(), c.campo) || undefined}
                            onDblClick={() => props.podeEditar && setEditando({ id: p().id, campo: c.campo })}
                          >
                            <Show
                              when={editando()?.id === p().id && editando()?.campo === c.campo}
                              fallback={textoCelula(p(), c.campo)}
                            >
                              <EditorCelula
                                inicial={textoCelula(p(), c.campo)}
                                numero={c.numero}
                                aoGravar={(t) => void gravar(p().id, c.campo, t)}
                                aoDesistir={() => setEditando(null)}
                              />
                            </Show>
                          </td>
                        )}
                      </For>
                      <td class="sticky right-0 z-10 border-b border-l border-edge px-0.5 text-center">
                        <AcoesLinha
                          podeEditar={props.podeEditar}
                          aoAbrir={() => props.aoSelecionar(p().id)}
                          aoEditar={() => props.aoEditar(p().id)}
                          aoExcluir={() => props.aoExcluir(p())}
                        />
                      </td>
                    </tr>
                  )}
                </Match>
              </Switch>
            )}
          </For>
        </tbody>
      </table>
    </div>
  )
}

/** Menu "⋯" no fim da linha: abrir, editar, excluir. */
function AcoesLinha(props: { podeEditar: boolean; aoAbrir: () => void; aoEditar: () => void; aoExcluir: () => void }) {
  return (
    <Dropdown>
      <Dropdown.Trigger
        as="button"
        class="px-1.5 py-0.5 rounded text-ink-muted hover:bg-hover hover:text-ink leading-none"
        aria-label="Ações do pedido"
        title="Ações do pedido"
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        ⋯
      </Dropdown.Trigger>
      <Dropdown.Content>
        <Dropdown.Item onSelect={props.aoAbrir}>Abrir detalhe</Dropdown.Item>
        <Show when={props.podeEditar}>
          <Dropdown.Item onSelect={props.aoEditar}>Editar…</Dropdown.Item>
          <Dropdown.Item onSelect={props.aoExcluir}>
            <span class="text-failure">Excluir pedido…</span>
          </Dropdown.Item>
        </Show>
      </Dropdown.Content>
    </Dropdown>
  )
}

function EditorCelula(props: { inicial: string; numero?: boolean; aoGravar: (t: string) => void; aoDesistir: () => void }) {
  let feito = false
  const fim = (gravar: boolean, valor: string) => {
    if (feito) return
    feito = true
    if (gravar) props.aoGravar(valor)
    else props.aoDesistir()
  }
  return (
    <input
      ref={(el) => requestAnimationFrame(() => (el.focus(), el.select()))}
      class="w-full min-w-0 bg-panel text-ink rounded px-1 -mx-1 outline outline-2 outline-accent"
      classList={{ 'text-right': props.numero }}
      value={props.inicial}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') fim(true, e.currentTarget.value)
        else if (e.key === 'Escape') fim(false, '')
      }}
      onBlur={(e) => fim(true, e.currentTarget.value)}
    />
  )
}

/** Ponto com a cor do status; clique abre a lista das 5 cores (D1). */
export function PontoStatus(props: {
  status: StatusPedido[] | undefined
  atual: number | null
  podeEditar: boolean
  aoTrocar: (id: number) => void
}) {
  const atual = () => props.status?.find((s) => s.id === props.atual)
  const ponto = (cor?: string) => (
    <span
      class="block size-3.5 rounded-full border"
      classList={{ 'border-black/25': !!cor, 'border-dashed border-ink-subtle': !cor }}
      style={{ background: cor ?? 'transparent' }}
    />
  )
  return (
    <Show when={props.podeEditar} fallback={<span title={atual()?.nome ?? 'Sem cor (classificar)'}>{ponto(atual() && corExibicao(atual()!))}</span>}>
      <Dropdown>
        <Dropdown.Trigger
          as="button"
          class="p-1 rounded hover:bg-hover"
          aria-label={`Status: ${atual()?.nome ?? 'sem cor'} — trocar`}
          title={atual()?.nome ?? 'Sem cor (classificar)'}
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          {ponto(atual() && corExibicao(atual()!))}
        </Dropdown.Trigger>
        <Dropdown.Content>
          <For each={props.status}>
            {(s) => (
              <Dropdown.Item onSelect={() => props.aoTrocar(s.id)}>
                <span class="flex items-center gap-2">
                  {ponto(corExibicao(s))}
                  <span classList={{ 'font-medium': s.id === props.atual }}>{s.nome}</span>
                </span>
              </Dropdown.Item>
            )}
          </For>
        </Dropdown.Content>
      </Dropdown>
    </Show>
  )
}
